/**
 * Spot illustrations — pequenas ilustrações isoladas para cards/sections.
 * Tudo SVG inline, sem dependência externa.
 */

interface SpotProps {
  className?: string;
}

/** Pilha de livros colorida (educacional) */
export function BookStack({ className }: SpotProps) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <defs>
        <linearGradient id="bs-v" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="hsl(255 80% 65%)" />
          <stop offset="1" stopColor="hsl(280 70% 55%)" />
        </linearGradient>
      </defs>
      <rect x="14" y="76" width="92" height="20" rx="5" fill="hsl(45 95% 70%)" />
      <rect x="22" y="54" width="76" height="20" rx="5" fill="hsl(15 90% 70%)" />
      <rect x="18" y="32" width="84" height="20" rx="5" fill="hsl(340 85% 72%)" />
      <g transform="translate(60 24)">
        <ellipse cx="0" cy="6" rx="22" ry="5" fill="hsl(255 30% 15%)" />
        <polygon points="-28 0 28 0 22 -8 -22 -8" fill="url(#bs-v)" />
        <circle cx="20" cy="-4" r="3" fill="hsl(45 95% 60%)" />
      </g>
    </svg>
  );
}

/** Sparkle pattern - decorativo, vibe Z */
export function SparkleCluster({ className }: SpotProps) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <g>
        <path
          d="M 60 20 Q 66 36 80 42 Q 66 48 60 64 Q 54 48 40 42 Q 54 36 60 20 Z"
          fill="hsl(255 78% 62%)"
        />
        <path
          d="M 30 70 Q 33 78 40 81 Q 33 84 30 92 Q 27 84 20 81 Q 27 78 30 70 Z"
          fill="hsl(340 85% 70%)"
        />
        <path
          d="M 90 80 Q 92 86 97 88 Q 92 90 90 96 Q 88 90 83 88 Q 88 86 90 80 Z"
          fill="hsl(45 95% 65%)"
        />
      </g>
    </svg>
  );
}

/** Cérebro estilizado (psicologia) */
export function BrainSpot({ className }: SpotProps) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <defs>
        <linearGradient id="brain-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="hsl(340 85% 72%)" />
          <stop offset="1" stopColor="hsl(15 90% 70%)" />
        </linearGradient>
      </defs>
      <path
        d="M 35 40 Q 28 32 36 26 Q 42 20 52 24 Q 60 18 70 24 Q 80 20 86 28 Q 92 36 86 44 Q 92 52 86 60 Q 88 70 78 74 Q 70 80 60 76 Q 50 80 42 74 Q 32 70 34 60 Q 28 52 35 40 Z"
        fill="url(#brain-g)"
      />
      <path
        d="M 60 28 L 60 76 M 42 36 Q 52 42 60 38 M 78 36 Q 68 42 60 38 M 42 56 Q 52 60 60 56 M 78 56 Q 68 60 60 56"
        stroke="hsl(340 50% 35%)"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        opacity="0.45"
      />
    </svg>
  );
}

/** Troféu / conquista */
export function TrophySpot({ className }: SpotProps) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <defs>
        <linearGradient id="trophy-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="hsl(45 95% 65%)" />
          <stop offset="1" stopColor="hsl(38 90% 50%)" />
        </linearGradient>
      </defs>
      {/* base */}
      <rect x="44" y="88" width="32" height="10" rx="3" fill="hsl(255 30% 15%)" />
      <rect x="50" y="78" width="20" height="12" fill="hsl(45 60% 40%)" />
      {/* taça */}
      <path
        d="M 36 28 L 36 50 Q 36 72 60 76 Q 84 72 84 50 L 84 28 Z"
        fill="url(#trophy-g)"
      />
      {/* alças */}
      <path
        d="M 36 32 Q 22 32 22 44 Q 22 56 36 56"
        stroke="hsl(45 95% 65%)"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M 84 32 Q 98 32 98 44 Q 98 56 84 56"
        stroke="hsl(45 95% 65%)"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
      {/* highlight */}
      <ellipse cx="50" cy="42" rx="8" ry="14" fill="white" opacity="0.35" />
      {/* estrela */}
      <path d="M 60 48 L 62 54 L 68 54 L 63 58 L 65 64 L 60 60 L 55 64 L 57 58 L 52 54 L 58 54 Z" fill="white" />
    </svg>
  );
}

/** Alvo / Diagnóstico */
export function TargetSpot({ className }: SpotProps) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <circle cx="60" cy="60" r="44" fill="hsl(255 78% 92%)" />
      <circle cx="60" cy="60" r="32" fill="hsl(255 78% 80%)" />
      <circle cx="60" cy="60" r="22" fill="hsl(255 78% 62%)" />
      <circle cx="60" cy="60" r="12" fill="hsl(340 85% 70%)" />
      <circle cx="60" cy="60" r="5" fill="white" />
      {/* dart */}
      <g transform="translate(70 50) rotate(35)">
        <rect x="-2" y="-18" width="4" height="22" fill="hsl(255 30% 15%)" rx="1" />
        <polygon points="-6,-18 6,-18 0,-26" fill="hsl(45 95% 65%)" />
      </g>
    </svg>
  );
}

/** Relógio / tempo */
export function ClockSpot({ className }: SpotProps) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <circle cx="60" cy="60" r="40" fill="hsl(160 60% 92%)" />
      <circle cx="60" cy="60" r="32" fill="white" stroke="hsl(160 60% 58%)" strokeWidth="3" />
      <line x1="60" y1="60" x2="60" y2="38" stroke="hsl(160 65% 38%)" strokeWidth="3" strokeLinecap="round" />
      <line x1="60" y1="60" x2="74" y2="60" stroke="hsl(160 65% 38%)" strokeWidth="3" strokeLinecap="round" />
      <circle cx="60" cy="60" r="3" fill="hsl(160 65% 38%)" />
    </svg>
  );
}
